import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from '@/components/shared/PageLayout';
import DrillLibrary from "@/components/drill-library/DrillLibrary";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, BarChart3, Grid3x3, Filter } from "lucide-react";
import { useClient } from "@/context/client-context";
import { useIsMobile } from "@/hooks/use-mobile";
import { StatisticsDrawer } from "@/components/drill-library/StatisticsDrawer";
import { useDataWithLoading } from "@/hooks/use-drill-data";
import { Drill } from "@/components/drill-library/types/drill-types";
import { getDrillDomain, getTagValues } from "@/utils/drill-taxonomy";
import { getTagColor } from "@/utils/drill-ui";
import { useSettings } from "@/components/settings/lib/settings-context";
import SuggestDrillsButton from "@/components/drill-library/SuggestDrillsButton";

export default function DrillsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { client } = useClient();
  const isMobile = useIsMobile();
  const [isStatisticsDrawerOpen, setIsStatisticsDrawerOpen] = useState(false);
  const [isOptionsDrawerOpen, setIsOptionsDrawerOpen] = useState(false);
  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [filteredDrillsCount, setFilteredDrillsCount] = useState<number | null>(null);
  const { data: drills } = useDataWithLoading<Drill>('drills');
  const { settings } = useSettings();
  const tagPreferences = {}; // TODO: Get from settings if available

  // Calculate library stats
  const libraryStats = React.useMemo(() => {
    if (!drills) {
      return {
        totalDrills: 0,
        positionalCount: 0,
        weightRoomCount: 0,
        rollingCount: 0,
        phase1Count: 0,
        phase2Count: 0,
        phase3Count: 0,
      };
    }
    
    const totalDrills = drills.length;
    const positionalCount = drills.filter(d => getDrillDomain(d) === 'Positional').length;
    const weightRoomCount = drills.filter(d => getDrillDomain(d) === 'Weightroom').length;
    const rollingCount = drills.filter(d => getDrillDomain(d) === 'Rolling').length;
    const phase1Count = drills.filter(d => getTagValues(d, 'Phase').includes('Phase 1')).length;
    const phase2Count = drills.filter(d => getTagValues(d, 'Phase').includes('Phase 2')).length;
    const phase3Count = drills.filter(d => getTagValues(d, 'Phase').includes('Phase 3')).length;
    
    return {
      totalDrills,
      positionalCount,
      weightRoomCount,
      rollingCount,
      phase1Count,
      phase2Count,
      phase3Count,
    };
  }, [drills]);
  
  return (
    <>
      <PageLayout
        title="Drill Library"
        subtitle="Browse and manage your exercise drills"
        compactContent
        headerActions={
          <div className="flex items-center justify-between gap-1.5 flex-nowrap w-full">
            {/* Left side: Statistics Button (mobile only) */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {isMobile && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsStatisticsDrawerOpen(true)}
                  className="h-10 px-2 gap-1 flex-shrink-0"
                  title="View Statistics"
                >
                  <BarChart3 className="h-4 w-4 flex-shrink-0" />
                  <span className="text-xs font-semibold whitespace-nowrap">
                    {filteredDrillsCount !== null ? filteredDrillsCount : libraryStats.totalDrills}
                  </span>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">drills</span>
                </Button>
              )}
            </div>
            
            {/* Right side: View and Filter (mobile) */}
            <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto">
              {/* View Button - Desktop and mobile */}
              <Button
                variant={isViewPanelOpen ? "default" : "outline"}
                size="sm"
                onClick={() => setIsViewPanelOpen(!isViewPanelOpen)}
                className={`h-10 flex-shrink-0 ${isMobile ? 'w-10 p-0' : 'px-2'}`}
                title="View Options"
              >
                <Grid3x3 className="h-4 w-4" />
                {!isMobile && <span className="ml-1.5 text-xs">View</span>}
              </Button>
              
              {/* Filter Button - Mobile only, next to view */}
              {isMobile && (
                <Button
                  variant={isFilterExpanded ? "default" : "outline"}
                  size="sm"
                  onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                  className="h-10 w-10 p-0 flex-shrink-0"
                  title="Filters"
                >
                  <Filter className="h-4 w-4" />
                </Button>
              )}
              
              {/* Suggest Drills Button — visible when a client is selected (all screen sizes) */}
              <SuggestDrillsButton />

              {/* Planner Button - Desktop only (lg+), experimental features only */}
              {!isMobile && settings.experimentalFeaturesEnabled && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/drill-planner')}
                  className="hidden lg:inline-flex h-10 px-2 flex-shrink-0"
                  title={client ? "Open Drill Planner" : "Select a client first to use the planner"}
                  disabled={!client}
                >
                  <LayoutDashboard className="h-4 w-4 sm:mr-1.5" />
                  <span className="hidden sm:inline text-xs">Planner</span>
                </Button>
              )}
            </div>
          </div>
        }
      >
        <DrillLibrary 
          user={user} 
          isOptionsDrawerOpen={isOptionsDrawerOpen}
          onOptionsDrawerToggle={setIsOptionsDrawerOpen}
          isViewPanelOpen={isViewPanelOpen}
          onViewPanelToggle={setIsViewPanelOpen}
          isFilterExpanded={isFilterExpanded}
          onFilterExpandedToggle={setIsFilterExpanded}
          onFilteredCountChange={setFilteredDrillsCount}
        />
      </PageLayout>
      
      {/* Statistics Drawer for Mobile */}
      {isMobile && (
        <StatisticsDrawer
          isOpen={isStatisticsDrawerOpen}
          onClose={() => setIsStatisticsDrawerOpen(false)}
          stats={libraryStats}
          getTagColor={getTagColor}
          tagPreferences={tagPreferences}
        />
      )}

    </>
  );
}